import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import PropTypes from 'prop-types';

// Components
import ErrorAnalyticsFallback from '@/components/layouts/ErrorFallbacks/ErrorAnalyticsFallback';
import { ChartWrapperContent } from '@/components/composite/Charts/ChartWrapperContent';
import { DeprecatedKpiBadge } from '@/components/base/Badge/DeprecatedKpiBadge';

// eslint-disable-next-line react/display-name
const ChartWrapper = ({
    isLoading,
    onReset = () => {
        /** */
    },
    data,
    isExport,
    ...restProps
}) => {
    const screenshotRef = useRef(null);
    const [activeFilter, setActiveFilter] = useState(data?.parameters?.type ?? data?.defaultFilter);

    useEffect(() => {
        if (activeFilter !== data.defaultFilter && !data?.parameters?.type) {
            setActiveFilter(data.defaultFilter);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.defaultFilter]);

    return (
        <ErrorBoundary FallbackComponent={ErrorAnalyticsFallback} onReset={() => onReset()}>
            <div
                ref={screenshotRef}
                className={clsx(
                    'relative flex h-full w-full flex-col rounded-md bg-white px-8 py-5 transition duration-200 hover:shadow-icoXL',
                    {
                        'border border-icoGray-500/30 text-icoBlack': isExport,
                        'dark:bg-icoDarkMode-anthracite dark:hover:shadow-icoDarkXL': !isExport
                    }
                )}>
                {isLoading ? (
                    <div className="flex h-full animate-pulse flex-col" data-testid="loading-data">
                        <div className="mb-5 flex items-center">
                            <span
                                className={clsx(
                                    'mr-5 block w-1/2 rounded bg-icoGray-300 px-4 py-4',
                                    { 'dark:bg-icoDarkMode-deepBlack': !isExport }
                                )}
                            />
                            <div className="align-center flex flex-1 justify-end space-x-3">
                                <span
                                    className={clsx(
                                        'block h-[2.5rem] w-[2.5rem] rounded-full bg-icoGray-300 py-2',
                                        { 'dark:bg-icoDarkMode-deepBlack': !isExport }
                                    )}
                                />
                                <span
                                    className={clsx(
                                        'block h-[2.5rem] w-[2.5rem] rounded-full bg-icoGray-300 py-2',
                                        { 'dark:bg-icoDarkMode-deepBlack': !isExport }
                                    )}
                                />
                            </div>
                        </div>
                        <span
                            className={clsx(
                                'mr-5 block w-full flex-1 rounded bg-icoGray-300 px-4 py-[6.875rem]',
                                { 'dark:bg-icoDarkMode-deepBlack': !isExport }
                            )}
                        />
                        <div className="mt-4 flex justify-end">
                            <span
                                className={clsx(
                                    'block w-1/3 rounded-full bg-icoGray-300 px-4 py-3',
                                    { 'dark:bg-icoDarkMode-deepBlack': !isExport }
                                )}
                            />
                        </div>
                    </div>
                ) : (
                    <>
                        <DeprecatedKpiBadge
                            className="-top-2 left-2"
                            deprecated={data.deprecated}
                            network={data.social}
                        />

                        <ChartWrapperContent
                            screenshotRef={screenshotRef}
                            data={data}
                            activeFilter={activeFilter}
                            setActiveFilter={setActiveFilter}
                            isExport={isExport}
                            {...restProps}
                        />
                    </>
                )}
            </div>
        </ErrorBoundary>
    );
};

ChartWrapper.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    isLoading: PropTypes.bool,
    onReset: PropTypes.func,
    data: PropTypes.object,
    isDashboard: PropTypes.bool,
    legendType: PropTypes.string,
    setRequestParams: PropTypes.func,
    setState: PropTypes.func,
    isComparativeViewActive: PropTypes.bool,
    toggleComparativeView: PropTypes.func
};

export default ChartWrapper;
